import React from 'react';
export type BlogStep = {
  number: number;
  title: string;
  image: string;
  description: string;
  tips: string[];
  material?: string;
  materialSlug?: string;
};
export type BlogPost = {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  image: string;
  type: 'guide' | 'article';
  quickInfo?: string[];
  materialsNeeded?: {
    name: string;
    use: string;
    slug: string | null;
  }[];
  steps?: BlogStep[];
  content?: React.ReactNode;
  seoBlocks?: {
    title: string;
    content: React.ReactNode;
  }[];
};
export const blogPosts: BlogPost[] = [{
  slug: 'walkway-gravel-vs-driveway-gravel',
  title: 'Walkway Gravel vs Driveway Gravel — Which One Do I Need?',
  date: 'June 26, 2026',
  excerpt: 'They look similar in the bin, but walkway gravel and driveway gravel are built for very different jobs. Here is how to pick the right one so your project lasts.',
  image: 'https://cdn.magicpatterns.com/uploads/bH95N6JcLS3ddKehbnVRWv/gravel_landscaping_in_tacoma.png',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1918" data-mp="2egetfxyequ9y">
        <p className="mb-4" data-id="element-1919" data-mp="2il4ppnwzaave">
          Walkway gravel and driveway gravel can look almost identical sitting
          in a bulk bin, but they are engineered for completely different loads.
          A path only has to carry foot traffic; a driveway has to hold up under
          the weight of cars, trucks, and the occasional delivery vehicle.
          Choosing the wrong one means a soft, rutted path or a driveway that
          scatters every time you back out. Here is how to tell them apart.
        </p>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/tpiwXZ7h5VKuZJngK4ptsh/driveway_gravel_spreading_in_tacoma.png" alt="Crushed gravel being spread across a driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-1920" data-mp="2shhimjkp8kp3" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1921" data-mp="3kytsxf7u1rpk">
          Walkway Gravel: Comfort & Looks
        </h3>
        <p className="mb-4" data-id="element-1922" data-mp="3uro8qdmhbpwv">
          Walkways prioritize a comfortable surface to walk on and a clean,
          finished appearance. Smaller, rounded, or decorative stone — like 3/8"
          pea gravel or crushed drain rock — feels good underfoot and packs into
          a stable, attractive path. Because a walkway never sees vehicle
          weight, you do not need a heavy structural base; a few inches of
          material over a compacted, weed-blocked sub-grade is usually plenty.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-1923" data-mp="325d1wl8a4srm">
          <li data-id="element-1924" data-mp="325whhvagj52h">
            <strong data-id="element-1925" data-mp="323abw174mzmw">Stone size:</strong> 3/8" pea gravel or small decorative
            rock — smooth and easy on bare feet.
          </li>
          <li data-id="element-1926" data-mp="2t56pz3s8c0uk">
            <strong data-id="element-1927" data-mp="2t4yftjnhr9n2">Depth:</strong> 2–3 inches over landscape fabric.
          </li>
          <li data-id="element-1928" data-mp="2o528pbjhz23a">
            <strong data-id="element-1929" data-mp="2o5c2793r3rdq">Best for:</strong> garden paths, side yards, patio borders,
            and fire-pit surrounds.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png" alt="Landscape fabric laid down before a gravel walkway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-1930" data-mp="18m4miwil7zhw" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1931" data-mp="1hm130autps1v">
          Driveway Gravel: Strength & Stability
        </h3>
        <p className="mb-4" data-id="element-1932" data-mp="oydpffrz01oq">
          Driveways need angular, "minus" crushed rock that locks together under
          weight. The fines (stone dust) in a minus product bind the larger
          stones into a firm, near-solid surface once compacted — exactly what
          you want when a vehicle rolls over it. A driveway is built in layers:
          a coarse 1.25" Minus base for structure, topped with a smoother 5/8"
          Minus driving surface that sheds water and resists potholes.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-1933" data-mp="28ukrpgz5y9oi">
          <li data-id="element-1934" data-mp="28v2me966gfub">
            <strong data-id="element-1935" data-mp="28sgg0cv0w6do">Base layer:</strong> 1.25" Minus crushed rock, 3–4 inches,
            compacted.
          </li>
          <li data-id="element-1936" data-mp="2hn4oebvwa46p">
            <strong data-id="element-1937" data-mp="2hno5jqdq0ojo">Top layer:</strong> 5/8" Minus crushed rock, 2–3 inches, the
            finished driving surface.
          </li>
          <li data-id="element-1938" data-mp="2cvmn1mpym1l7">
            <strong data-id="element-1939" data-mp="2cv4scuiy3vfe">Best for:</strong> driveways, parking pads, and any surface
            that carries vehicles.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png" alt="Finished compacted gravel driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-1940" data-mp="2mlfy8mm6weox" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1941" data-mp="2wiaqinrvdiur">
          Quick Answer
        </h3>
        <p className="mb-4" data-id="element-1942" data-mp="126nmqbr02828">
          If people walk on it, choose a smaller, comfortable stone like 3/8"
          pea gravel. If vehicles drive on it, use angular minus crushed rock in
          two compacted layers. When in doubt, give us a call — we will help you
          pick the right material and the right amount before you order.
        </p>
      </div>
}, {
  slug: 'perfect-gravel-driveway',
  title: 'How to Build a Perfect Gravel Driveway',
  date: 'May 24, 2026',
  excerpt: 'A complete step-by-step guide to transforming a dirt driveway into a professional, long-lasting gravel surface. No contractor needed.',
  image: 'https://cdn.magicpatterns.com/uploads/bH95N6JcLS3ddKehbnVRWv/gravel_landscaping_in_tacoma.png',
  type: 'guide',
  quickInfo: ['⏱ Weekend project', '📏 Any driveway size', '💰 ~$3–5/sq ft', '🔧 Basic tools needed'],
  materialsNeeded: [{
    name: '1.25" Minus Crushed Rock',
    use: 'Base layer — compacts into a solid foundation',
    slug: '1-25-minus'
  }, {
    name: '5/8" Minus Crushed Rock',
    use: 'Top layer — smooth, firm driving surface',
    slug: '5-8-minus'
  }, {
    name: 'Landscape Fabric',
    use: 'Weed barrier between soil and gravel',
    slug: null
  }, {
    name: 'Fill Dirt (if needed)',
    use: 'Level out low spots before gravel',
    slug: null
  }],
  steps: [{
    number: 1,
    title: 'Assess & Measure Your Driveway',
    image: 'https://cdn.magicpatterns.com/uploads/2ebE9ciuiVZZKZHNZLJiZ5/measuring_gravel_driveway.png',
    description: 'Start by measuring the length, width, and noting the current condition of your dirt driveway. Mark the boundaries with stakes and string. Calculate the square footage — this determines how much material you need.',
    tips: ['Measure length × width in feet', 'Plan for 4–6 inches of base material depth', 'Use our Calculator to estimate yards needed', 'Account for any slope or drainage needs']
  }, {
    number: 2,
    title: 'Clear & Grade the Surface',
    image: 'https://cdn.magicpatterns.com/uploads/gQYsAvdSYDiWUkNHGFHmQH/grading_driveway.png',
    description: 'Remove any vegetation, large rocks, and debris from the driveway area. Use a rake or skid steer to grade the surface so water drains to the sides. A slight crown (higher in the center) prevents puddles.',
    tips: ['Remove all grass, weeds, and roots', 'Create a 2% slope from center to edges', 'Fill any low spots with fill dirt', 'Compact the bare soil with a plate compactor']
  }, {
    number: 3,
    title: 'Install Landscape Fabric (Optional)',
    image: 'https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png',
    description: 'Lay commercial-grade landscape fabric over the graded dirt. This prevents weeds from growing up through your gravel and helps separate the soil from the rock layers. Overlap seams by 6–12 inches.',
    tips: ['Use commercial-grade fabric, not cheap weed barrier', 'Overlap seams by at least 6 inches', 'Secure with landscape staples every 3 feet', 'This step saves major headaches down the road']
  }, {
    number: 4,
    title: 'Spread the Base Layer — 1.25" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/tpiwXZ7h5VKuZJngK4ptsh/driveway_gravel_spreading_in_tacoma.png',
    description: 'The base layer is the foundation of your driveway. Spread 3–4 inches of 1.25" Minus crushed rock evenly across the surface. This coarser material locks together and creates a rock-solid base that won\'t shift.',
    tips: ['Order 1.25" Minus from our catalog', 'Spread evenly with a rake to 3–4" depth', 'Compact thoroughly with a plate compactor', 'Make multiple passes until the surface is firm'],
    material: '1.25" Minus',
    materialSlug: '1-25-minus'
  }, {
    number: 5,
    title: 'Compact the Base Layer',
    image: 'https://cdn.magicpatterns.com/uploads/hkbT29dFvc9BT49e5MH1GV/compacting_base_layer.png',
    description: 'Rent a plate compactor (available at most equipment rental shops) and make several passes over the base layer. Wet the material lightly before compacting — moisture helps the fines bind together for maximum density.',
    tips: ['Lightly wet the gravel before compacting', 'Make 3–4 passes in different directions', 'The surface should feel solid underfoot', 'Add more material to any low spots and re-compact']
  }, {
    number: 6,
    title: 'Add the Top Layer — 5/8" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/pqds5pqkfBofDzHJ4pjSBj/base_layer_compacting.png',
    description: 'Now spread 2–3 inches of 5/8" Minus crushed rock on top. This is the finished driving surface — it packs down tight, sheds water, and provides a smooth, firm surface for vehicles and foot traffic.',
    tips: ['Order 5/8" Minus — our most popular driveway gravel', 'Spread to 2–3" depth over the compacted base', 'Rake smooth and maintain the crown shape', 'Compact one final time for a professional finish'],
    material: '5/8" Minus',
    materialSlug: '5-8-minus'
  }, {
    number: 7,
    title: 'Final Compaction & Edging',
    image: 'https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png',
    description: 'Give the top layer a final compaction pass. Optionally, install edging (timber, steel, or stone) along the sides to keep gravel contained. Your new driveway is ready to use — it will continue to settle and harden over the first few weeks.',
    tips: ['Edging prevents gravel from migrating into the yard', 'Drive on it gently for the first week', 'Top off with a thin layer of 5/8" Minus annually', 'Keep drainage channels clear of debris']
  }],
  seoBlocks: [{
    title: 'Gravel Delivery Across Pierce & King Counties',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-1943" data-mp="39zu7lxcba3ac">
              Whether you're building a new gravel driveway in Puyallup,
              replacing a muddy parking pad in Bonney Lake, or resurfacing a
              rural road in Enumclaw, having the right materials delivered on
              time makes all the difference. 206Gravel provides same-day and
              next-day bulk gravel delivery from Tacoma to Seattle and
              everywhere in between — Lakewood, Federal Way, Auburn, Kent,
              Sumner, Edgewood, Renton, Bellevue, Kirkland, Redmond, and the
              surrounding Pierce and King County communities.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-1944" data-mp="1rb9gkor0rckk">
              Our dump trucks carry 5–12 yards per load and can place material
              directly on your driveway, jobsite, or backyard — no wheelbarrow
              relay required. We know the local soil conditions (hello, Puget
              Sound clay) and can recommend the right base and top layers for
              driveways that last through Pacific Northwest winters.
            </p>
          </>
  }, {
    title: 'Why Buy Gravel Locally in the Puget Sound?',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-1945" data-mp="gn2585r5iiiw">
              Big-box stores sell gravel by the bag — but for driveways and
              serious landscaping, you need bulk delivery by the yard. Buying
              locally from 206Gravel means lower delivery costs, fresher
              material sourced from regional quarries, and drivers who know how
              to navigate Tacoma's hills, Seattle's tight streets, King County's
              rural roads, and everything in between.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-1946" data-mp="2yo237t56yoe5">
              We're based in the 206 area code and deliver to King County and
              King County daily. No middlemen, no markups from national chains —
              just honest pricing on crushed rock, drain rock, topsoil, bark,
              and recycled aggregates delivered straight to your property.
            </p>
          </>
  }]
}, {
  slug: 'best-base-for-gravel-driveway',
  title: 'Best Base for a Gravel Driveway',
  date: 'May 19, 2026',
  excerpt: 'The best base for a gravel driveway is a large angular crushed stone (around 1 to 2 inches) for the sub-base, laid...',
  image: 'https://cdn.magicpatterns.com/uploads/8BfgzDv9jVyH7TX3BiStiF/Gravel2BDriveway2BHero2BDesktop.webp',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1947" data-mp="wscgyg9dwdb9">
        <p className="mb-4" data-id="element-1948" data-mp="1ug4mbxiliktx">
          The foundation of any long-lasting gravel driveway is its base layer.
          Without a proper base, your top gravel will sink into the mud,
          creating potholes and requiring constant maintenance.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1949" data-mp="1koqjpgvcl4wk">
          Why 1.25" Minus is the Standard
        </h3>
        <p className="mb-4" data-id="element-1950" data-mp="2d6cc7dxabes6">
          For most residential driveways in the Pacific Northwest, 1.25" Minus
          crushed rock is the go-to base material. The "minus" means it contains
          stone dust (fines) along with the larger 1.25-inch angular rocks. When
          compacted, these fines lock the larger stones together like concrete.
        </p>
        <p className="mb-4" data-id="element-1951" data-mp="1x0sq43wd9rgt">
          Always ensure you lay down at least 3-4 inches of base rock and
          compact it thoroughly before adding your top layer.
        </p>
      </div>
}, {
  slug: 'how-much-is-a-load-of-gravel',
  title: 'How Much is a Load of Gravel?',
  date: 'May 11, 2026',
  excerpt: 'A standard tandem-axle dump truck load of gravel (around 12 to 15 tons) costs around $400 to $1,000 delivered in 2026...',
  image: 'https://cdn.magicpatterns.com/uploads/pPaGG3aiENovvC8wi26rwz/TJHunt5-700x467.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1952" data-mp="26x3zom4ko37k">
        <p className="mb-4" data-id="element-1953" data-mp="2umq3jmvw36lw">
          The cost of a load of gravel depends heavily on the material type,
          your location, and the size of the truck delivering it.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1954" data-mp="nypnzpzuk7zf">
          Truck Sizes
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-1955" data-mp="j0o2yziv1ylb">
          <li data-id="element-1956" data-mp="iy1xd5fj5t5q">
            <strong data-id="element-1957" data-mp="wr1efbuynvfe">Solo Dump Truck:</strong> Carries about 5 to 7 yards (6-9
            tons). Ideal for smaller residential projects.
          </li>
          <li data-id="element-1958" data-mp="sq7qwjlfkin1">
            <strong data-id="element-1959" data-mp="srsko6zx7xju">Tandem-Axle Dump Truck:</strong> Carries about 10 to 12
            yards (12-15 tons). The standard for most driveway installations.
          </li>
          <li data-id="element-1960" data-mp="2213ifis3gc">
            <strong data-id="element-1961" data-mp="2lcshektvlx">Truck and Pup:</strong> Carries up to 24 yards. Best for
            large rural driveways or commercial jobs.
          </li>
        </ul>
        <p className="mb-4" data-id="element-1962" data-mp="9172qxmdocwq">
          Remember that delivery fees are often a significant portion of the
          total cost, which is why buying locally from a supplier in your
          delivery zone is crucial.
        </p>
      </div>
}];